import { create, updateField, updateFields, update, remove, getById, getAll, getBySingleQuery, updateRecord, addRecordToTop, addValueToChildArray, getByIdQuery, getByDoubleQuery } from '../utils/dbUtils';
import { isBooleanTrue, isListEmpty, isListLongerThanOne, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from "../utils/objectUtils";
import { collection, getCountFromServer, getDocs, limit, orderBy, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import User from './User'
import { toLower } from 'lodash';
import CommunityMember from './CommunityMember';
import CommunityThread from './CommunityThread';
import Conversation from './Conversation';

class Community {
    static collectionName = 'communities';

    adminIds = []; // array of user ids
    moderatorIds = []; // array of user ids
    memberIds = []; // array of user ids
    pendingMemberIds = []; // array of user ids who requested to join

    name = "New Community";
    slug = null;
    description = ""; // group description
    coverPhotoUrl = ""; // group cover photo

    type = ""; // e.g., "COMMUNITY", "GROUP"
    privacy = "PUBLIC"; // "PUBLIC", "PRIVATE", "HIDDEN"

    metadata = {};
    tags = [];

    rules = []; // array of strings
    announcements = []; // array of announcement objects

    joinQuestions = []; // array of questions for joining

    testMode = false;

    constructor(community) {
        if (isObjectNotEmpty(community)) {
            if (isStringNotEmpty(community.name)) { this.name = community.name }
            else { this.name = "New Community" };

            if (isStringNotEmpty(community.slug)) { this.slug = toLower(community.slug); }

            if (isListNotEmpty(community.adminIds)) { this.adminIds = community.adminIds }
            if (isListNotEmpty(community.moderatorIds)) { this.moderatorIds = community.moderatorIds }
            if (isListNotEmpty(community.memberIds)) { this.memberIds = community.memberIds }
            if (isListNotEmpty(community.pendingMemberIds)) { this.pendingMemberIds = community.pendingMemberIds }

            if (isStringNotEmpty(community.description)) { this.description = community.description }
            if (isStringNotEmpty(community.coverPhotoUrl)) { this.coverPhotoUrl = community.coverPhotoUrl }

            if (isStringNotEmpty(community.privacy)) { this.privacy = community.privacy }

            if (isListNotEmpty(community.metadata)) { this.metadata = community.metadata }
            if (isListNotEmpty(community.tags)) { this.tags = community.tags }
            if (isListNotEmpty(community.rules)) { this.rules = community.rules }
            if (isListNotEmpty(community.announcements)) { this.announcements = community.announcements }
            if (isListNotEmpty(community.joinQuestions)) { this.joinQuestions = community.joinQuestions }

            if (isStringNotEmpty(community.type)) { this.type = community.type }

            if (isBooleanTrue(community.testMode)) { this.testMode = true; }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            slug: this.slug,
            description: this.description,
            coverPhotoUrl: this.coverPhotoUrl,

            adminIds: this.adminIds,
            moderatorIds: this.moderatorIds,
            memberIds: this.memberIds,
            pendingMemberIds: this.pendingMemberIds,

            metadata: this.metadata,
            tags: this.tags,

            type: this.type,

            privacy: this.privacy,
            rules: this.rules,
            announcements: this.announcements,
            joinQuestions: this.joinQuestions,

            testMode: this.testMode
        }
    }

    isValid = () => {
        if (isStringEmpty(this.name)) {
            throw new Error('Community name is required.');
        }

        if (isListEmpty(this.adminIds)) {
            throw new Error('Community must have at least 1 admin');
        }

        return true;
    }

    create = async () => {
        if (this.isValid()) {
            // Create the community record
            const newCommunity = await create(Community.collectionName, this.toDto());

            if (newCommunity) {
                try {
                    // If there are initial members, create their records
                    if (Array.isArray(newCommunity.memberIds) && newCommunity.memberIds.length > 0) {
                        await Promise.all(
                            newCommunity.memberIds
                                .map(userId => CommunityMember.create(newCommunity.id, userId, 'ACTIVE'))
                        );
                    }

                    // If there are initial admins, set their roles
                    if (Array.isArray(newCommunity.adminIds) && newCommunity.adminIds.length > 0) {
                        await Promise.all(
                            newCommunity.adminIds
                                .map(userId => CommunityMember.updateRole(newCommunity.id, userId, 'ADMIN', true))
                        );
                    }

                    // If there are initial moderators, set their roles
                    if (Array.isArray(newCommunity.moderatorIds) && newCommunity.moderatorIds.length > 0) {
                        await Promise.all(
                            newCommunity.moderatorIds.map(userId =>
                                CommunityMember.updateRole(newCommunity.id, userId, 'MODERATOR', true))
                        );
                    }

                    return newCommunity;
                } catch (error) {
                    // If member creation fails, delete the community to maintain consistency
                    await remove(this.collectionName, newCommunity.id);
                    throw new Error(`Failed to create community members: ${error.message}`);
                }
            }
        }

        return null;
    }

    static sendMessage = async (encounterId, message = null, mediaRefId = null, profile, userId, testMode = false) => {

    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Community.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static updateFields = async (id, updatedFields) => {
        if (isStringNotEmpty(id)) {
            return await updateFields(Community.collectionName, id, updatedFields);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedCommunity, oldObj) => {
        const updatedObj = new Community(updatedCommunity);
        if (updatedObj.isValid() && id === updatedCommunity.id) {
            const success = await update(Community.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {
                return { ...oldObj, ...updatedObj.toDto() }
            } else {
                throw new Error('Failed to update community.');
            }
        } else {
            throw new Error('id and id field of the updated community must match.')
        }
    }

    static delete = async (communityId) => {
        try {
            // Get all threads for this community
            const threads = await CommunityThread.getByCommunityId(communityId);

            // Get all members for this community
            const members = await CommunityMember.getByCommunityId(communityId);

            // Get all conversations for this community
            const conversations = await Conversation.getByCommunityId(communityId);

            // Delete all threads
            if (isListNotEmpty(threads)) {
                await Promise.all(threads.map(thread => CommunityThread.delete(thread.id)));
            }

            // Remove all members from conversations
            if (isListNotEmpty(conversations)) {
                await Promise.all(conversations.map(async conversation => {
                    const participantIds = conversation.participantIds || [];
                    await Promise.all(participantIds.map(userId =>
                        Conversation.removeParticipant(conversation.id, userId, true)
                    ));
                    await Conversation.delete(conversation.id);
                }));
            }

            // Delete all member records
            if (isListNotEmpty(members)) {
                await Promise.all(members.map(member => CommunityMember.delete(member.id)));
            }

            // Finally delete the community itself
            await remove(this.collectionName, communityId);

            return true;
        } catch (error) {
            console.error('Error deleting community:', error);
            throw new Error(`Failed to delete community: ${error.message}`);
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Community.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getBySlug = async (slug) => {
        let list = await getBySingleQuery(Community.collectionName, 'slug', toLower(slug), '==');
        if (isListNotEmpty(list)) {
            return list[0];
        }

        return null;
    }

    static getAll = async () => {
        return await getAll(Community.collectionName);
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Community.collectionName, ids, 'in');
    }

    static getByPrivacy = async (privacy) => {
        return await getBySingleQuery(Community.collectionName, 'privacy', privacy, '==');
    }

    static getByType = async (type) => {
        return await getBySingleQuery(Community.collectionName, 'type', type, '==');
    }

    static getByPrivacyAndType = async (privacy, type) => {
        return await getByDoubleQuery(Community.collectionName, 'privacy', privacy, '==', 'type', type, '==');
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Community.collectionName, fieldName, fieldValue, comparer);
    }

    static getByMemberId = async (userId) => {
        // Instead of querying Community directly, get active members from CommunityMember
        const activeMembers = await CommunityMember.getByUserId(userId);
        const communityIds = activeMembers.map(member => member.communityId);
        return communityIds.length > 0 ? await this.getByIds(communityIds) : [];
    }

    static getByAdminId = async (userId) => {
        // Use CommunityMember to get communities where user is admin
        const adminMembers = await CommunityMember.getByUserIdAndRole(userId, 'ADMIN');
        const communityIds = adminMembers.map(member => member.communityId);
        return communityIds.length > 0 ? await this.getByIds(communityIds) : [];
    }

    static getByModeratorId = async (userId) => {
        // Use CommunityMember to get communities where user is moderator
        const moderatorMembers = await CommunityMember.getByUserIdAndRole(userId, 'MODERATOR');
        const communityIds = moderatorMembers.map(member => member.communityId);
        return communityIds.length > 0 ? await this.getByIds(communityIds) : [];
    }

    static search = async (searchCriteria) => {
        let { name, tag } = searchCriteria;

        let queryConstraints = [];

        if (isStringNotEmptyOrZeroLength(name)) {
            queryConstraints.push(where('name', '==', name));
        }

        if (isStringNotEmptyOrZeroLength(tag)) {
            queryConstraints.push(where('tags', 'array-contains', tag));
        }

        queryConstraints.push(orderBy('createdAt', 'asc'));
        queryConstraints.push(orderBy('__name__', 'asc'));

        return await this.getByQueryConstraints(queryConstraints);
    }

    static getCount = async (queryParameters) => {
        const { testMode, name, tag } = queryParameters;

        let queryConstraints = [];

        if (isNotNullOrUndefined(testMode)) {
            queryConstraints.push(where('testMode', '==', isBooleanTrue(testMode)));
        }

        if (isStringNotEmptyOrZeroLength(name)) {
            queryConstraints.push(where('name', '==', name));
        }

        if (isStringNotEmptyOrZeroLength(tag)) {
            queryConstraints.push(where('tags', 'array-contains', tag));
        }

        let q = query(collection(db, Community.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, orderByField = 'createdAt') => {
        const { testMode, name, tag } = queryParameters;

        let queryConstraints = [];

        if (isNotNullOrUndefined(testMode)) {
            queryConstraints.push(where('testMode', '==', isBooleanTrue(testMode)));
        }

        if (isStringNotEmptyOrZeroLength(name)) {
            queryConstraints.push(where('name', '==', name));
        }

        if (isStringNotEmptyOrZeroLength(tag)) {
            queryConstraints.push(where('tags', 'array-contains', tag));
        }

        const q = query(collection(db, Community.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));


        let snapshot = await getDocs(q);
        return snapshot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;

            let totalCount = await Community.getCount(queryParameters);
            let totalPages = Math.ceil(totalCount / 10000.0);

            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {

                let list = await Community.getPaginated(queryParameters, 10000, 'createdAt');

                allRecords = [...allRecords, ...list];

                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }

    // Add announcement
    static addAnnouncement = async (communityId, announcement) => {
        const community = await this.getById(communityId);

        if (isObjectNotEmpty(community)) {
            let updatedAnnouncements = addRecordToTop(announcement.id, announcement, community?.announcements);
            return await this.updateField(communityId, 'announcements', updatedAnnouncements);
        }

        return false;
    }
};

export default Community;